import React, { useRef, useEffect } from 'react';
import { ValidationIndicator } from './ValidationIndicator';

interface CLBGTableProps {
  field: any;
  companyInfo: any;
  formData: Record<string, any>;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  hasFieldError?: (fieldName: string, contextRef?: string) => boolean;
}

// Helper to format date strings
const formatDateForContext = (dateStr: string): string => {
  if (/^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  return dateStr.replace(/-/g, '');
};

// Helper function to get period context
const getPeriodContext = (item: any, companyInfo: any, member: string): string => {
  const period = companyInfo.currentPeriod || companyInfo.reporting_period;
  if (!period) {
    return `context_${member}`;
  }

  const formattedStart = formatDateForContext(period.start);
  const formattedEnd = formatDateForContext(period.end);
  const cleanMember = member.startsWith('_') ? member.substring(1) : member;
  const memberSuffix = cleanMember ? `_${cleanMember}` : '';

  if (item.properties?.period_type === 'duration') {
    return `fromto_${formattedStart}_${formattedEnd}${memberSuffix}`;
  } else {
    return `asof_${formattedEnd}${memberSuffix}`;
  }
};

/**
 * CLBGTable - Category of object for Companies Limited By Guarantee
 * Matches 1.0 logic from form_macros-tailwind.html lines 752-833
 */
const CLBGTableComponent: React.FC<CLBGTableProps> = ({
  field,
  companyInfo,
  formData,
  onFieldChange,
  hasFieldError
}) => {
  // Extract table structure (matches line 753-756)
  const table = field.structure?.children?.[0]?.structure?.children?.[0];
  const axis = table?.structure?.children?.[0];
  const members = axis?.structure?.children?.[0]?.structure?.children || [];
  const lineItems = field.structure?.children?.[0]?.structure?.children?.[1]?.structure?.children || [];

  // Category options (matches lines 789-797)
  const categoryOptions = [
    'Promoting any other objects useful for the community or country',
    'Promoting pension or superannuation schemes',
    'Promoting charity',
    'Promoting religion',
    'Promoting science',
    'Promoting art',
    'Promoting commerce and industry',
    'Providing recreation or amusement'
  ];

  // Subcategory options (matches lines 807-812)
  const subcategoryOptions = [
    'Health',
    'Environment',
    'Education',
    'Sports',
    'Research',
    'Social'
  ];

  // Get member label for field naming
  const getMemberLabel = (index: number) => {
    if (index === 0) return 'One';
    if (index === 1) return 'Two';
    return 'Three';
  };

  // Component for table cells
  const CLBGTableCell: React.FC<{
    item: any;
    member: any;
    memberIndex: number;
    formData: Record<string, any>;
    hasError: boolean;
  }> = ({ item, member, memberIndex, formData, hasError }) => {
    const contextRef = getPeriodContext(item, companyInfo, `_${member.name}`);
    const memberLabel = getMemberLabel(memberIndex);
    const fieldNameWithSuffix = `${item.name}_${member.name}`;
    const dataKey = `${item.qname}_${contextRef}`;
    const initialValue = formData[dataKey] || '';
    const selectRef = useRef<HTMLSelectElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync DOM with ref when data loads
    useEffect(() => {
      const element = selectRef.current || inputRef.current;
      if (element) {
        const refValue = formData[dataKey] || '';
        if (element.value !== refValue) {
          element.value = refValue;
        }
      }
    }, [formData, dataKey]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      onFieldChange(fieldNameWithSuffix, contextRef, e.target.value, item.qname);
    };

    // Category dropdown (matches lines 780-797)
    if (item.qname === 'ssmt:DisclosureOnCategoriesOfObjectOfCompaniesLimitedByGuarantee') {
      return (
        <select
          ref={selectRef}
          className={`form-control ${hasError ? 'has-error' : ''}`}
          name={fieldNameWithSuffix}
          data-qname={item.qname}
          data-period-type={item.properties?.period_type}
          data-type={item.properties?.data_type}
          data-balance={item.properties?.balance}
          data-context-ref={contextRef}
          defaultValue={initialValue}
          onChange={handleChange}
        >
          <option value=""></option>
          {categoryOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    // Subcategory dropdown (matches lines 798-813)
    if (item.qname === 'ssmt:DisclosureOnSubCategoriesOfObjectOfCompaniesLimitedByGuarantee') {
      return (
        <select
          ref={selectRef}
          className={`form-control ${hasError ? 'has-error' : ''}`}
          name={fieldNameWithSuffix}
          data-qname={item.qname}
          data-period-type={item.properties?.period_type}
          data-type={item.properties?.data_type}
          data-balance={item.properties?.balance}
          data-context-ref={contextRef}
          defaultValue={initialValue}
          onChange={handleChange}
        >
          <option value=""></option>
          {subcategoryOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    // Regular text input (matches lines 815-823)
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${hasError ? 'has-error' : ''}`}
          name={fieldNameWithSuffix}
          data-qname={item.qname}
          data-period-type={item.properties?.period_type}
          data-type={item.properties?.data_type}
          data-balance={item.properties?.balance}
          data-context-ref={contextRef}
          defaultValue={initialValue}
          onChange={handleChange}
          placeholder={`Enter ${item.standard_label}`}
        />
        <ValidationIndicator fieldName={fieldNameWithSuffix} contextRef={contextRef} />
      </div>
    );
  };

  return (
    <div className="clbg-table-section">
      <h3 className="section-header">{table?.standard_label}</h3>
      
      <div className="card-table">
        <table className="table-element" data-datatable-table="true">
          <thead>
            <tr>
              <th></th>
              {members.map((member: any) => (
                <th key={member.name}>
                  <span className="sort">
                    <span className="sort-label">{member.standard_label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="form-element">
            {lineItems.map((item: any) => {
              if (item.properties?.abstract) return null;
              
              return (
                <tr key={item.name}>
                  <td className="table-title-0 c-black">{item.standard_label}</td>
                  {members.map((member: any, memberIndex: number) => {
                    const contextRef = getPeriodContext(item, companyInfo, `_${member.name}`);
                    const fieldNameWithSuffix = `${item.name}_${member.name}`;
                    const fieldHasError = hasFieldError ? hasFieldError(fieldNameWithSuffix, contextRef) : false;

                    return (
                      <td key={`${item.name}_${member.name}`} className="table-input">
                        <CLBGTableCell
                          item={item}
                          member={member}
                          memberIndex={memberIndex}
                          formData={formData}
                          hasError={fieldHasError}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders when parent state changes
export const CLBGTable = React.memo(CLBGTableComponent);